import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';

import {
	askDiscardChanges,
	dismissDiscardConfirms,
	useUnsavedChangesRegistry,
} from '../contexts/UnsavedChangesContext';

const defaultMessage = 'You have unsaved changes. Leave without saving?';

const defaultSerialize = (value) => {
	try {
		return JSON.stringify(value ?? null);
	}
	catch (err) {
		console.warn('Failed to serialize snapshot for dirty tracking', err);
		return String(value);
	}
};

// Leaving a page is a change of path. A change of query alone (?tab= on the
// card and collection pages, a list's filter) keeps the form on screen, so it
// is not held.
const leavesThePage = ({ currentLocation, nextLocation }) => (
	!currentLocation || !nextLocation || currentLocation.pathname !== nextLocation.pathname
);

/**
 * Unsaved-changes tracking for one form.
 *
 * `snapshot` is the form's value; it is dirty while its serialization differs
 * from the baseline (the snapshot at mount, or whatever `resetDirty` last
 * set). While dirty:
 *
 * - closing or reloading the tab asks (`beforeunload`);
 * - an in-app navigation that leaves the page — a rail link, the back button,
 *   the form's own `navigate()` — is held by the one router blocker
 *   (`UnsavedChangesProvider`) and asks "Discard unsaved changes?";
 * - `confirmDiscard()` asks the same question for a Cancel or Esc that does
 *   not navigate (a modal closing), resolving true when it is fine to drop
 *   the typing.
 *
 * Returns:
 * - `isDirty`
 * - `resetDirty(next?)` — the new baseline (the saved value; the current
 *   snapshot when omitted). It also lets the CURRENT state leave without
 *   asking, until it next changes: a save that marks the form pristine and
 *   navigates in the same tick goes through even when the server's echo
 *   serializes differently from what was typed.
 * - `allowNavigation()` — only the second half of that: this exact state may
 *   leave without asking (after a delete, say).
 * - `confirmDiscard(options?)` — Promise<boolean>; resolves true at once when
 *   clean. On Discard the current state is allowed to leave, so the
 *   `navigate()` that follows is not asked a second time.
 * - `guardDiscard(fn, options?)` — what a Cancel or Esc handler wants: runs
 *   `fn` straight away when clean, after Discard when dirty, never on Keep
 *   editing.
 *
 * Options: `enabled` (a modal passes its `open`, so a closed modal holding
 * stale state never blocks), `serializer`, `message` (the beforeunload text,
 * which browsers mostly ignore), `shouldBlock({ currentLocation,
 * nextLocation })` to widen or narrow what counts as leaving (default: a
 * change of pathname), `onBlocked({ proceed, reset, location })` for a
 * form with a leave prompt of its own — return true when it has taken the
 * navigation over; its `proceed` answers for this form only, and any other
 * dirty form on the page is still asked — and `baselineKey` (below).
 */
export function useDirtyForm(snapshot, options = {}) {
	const {
		message = defaultMessage,
		serializer = defaultSerialize,
		enabled = true,
		shouldBlock,
		onBlocked,
		baselineKey,
	} = options;

	const serialized = useMemo(() => serializer(snapshot), [snapshot, serializer]);

	const initialRef = useRef(serialized);
	// A new baselineKey makes THIS render's snapshot the baseline — for a
	// form that seeds itself in an effect (a modal re-seeding on open): bump
	// the key in the same effect that sets the seeded values.
	const baselineKeyRef = useRef(baselineKey);
	if (!Object.is(baselineKeyRef.current, baselineKey)) {
		baselineKeyRef.current = baselineKey;
		initialRef.current = serialized;
	}
	// A state that may leave without asking (just saved, or discarded). It
	// lapses the moment the snapshot moves on.
	const acceptedRef = useRef(null);
	if (acceptedRef.current !== null && acceptedRef.current !== serialized) acceptedRef.current = null;

	const latest = useRef({});
	latest.current = { snapshot, serialized, enabled, serializer, shouldBlock, onBlocked };

	const [, setBaselineVersion] = useState(0);

	const computeDirty = useCallback(() => {
		const { serialized: current, enabled: on } = latest.current;
		return Boolean(on) && current !== initialRef.current && current !== acceptedRef.current;
	}, []);

	const isDirty = Boolean(enabled) && serialized !== initialRef.current && serialized !== acceptedRef.current;

	const allowNavigation = useCallback(() => {
		acceptedRef.current = latest.current.serialized;
		setBaselineVersion(version => version + 1);
	}, []);

	const resetDirty = useCallback((nextSnapshot) => {
		const { snapshot: current, serializer: serialize } = latest.current;
		initialRef.current = serialize(typeof nextSnapshot === 'undefined' ? current : nextSnapshot);
		allowNavigation();
	}, [allowNavigation]);

	const { modal } = App.useApp();
	const modalRef = useRef(modal);
	modalRef.current = modal;

	// This form's Cancel confirm lives in the antd App above the router, not
	// in the form: it goes when the form does (and when a navigation lands —
	// UnsavedChangesProvider), so its Discard can never run a dead form's
	// navigate(). One at a time: a second Cancel joins the open question.
	const ownerRef = useRef(null);
	if (!ownerRef.current) ownerRef.current = {};
	const mountedRef = useRef(false);
	useEffect(() => {
		mountedRef.current = true;
		const owner = ownerRef.current;
		return () => {
			mountedRef.current = false;
			dismissDiscardConfirms(owner);
		};
	}, []);

	const confirmDiscard = useCallback((askOptions) => {
		if (!computeDirty()) return Promise.resolve(true);
		return askDiscardChanges(modalRef.current, { ...askOptions, owner: ownerRef.current }).then(ok => {
			// A stale answer (the form is gone) is Keep editing.
			if (!ok || !mountedRef.current) return false;
			allowNavigation();
			return true;
		});
	}, [computeDirty, allowNavigation]);

	const guardDiscard = useCallback((fn, askOptions) => {
		if (!computeDirty()) {
			fn();
			return;
		}
		confirmDiscard(askOptions).then(ok => { if (ok) fn(); });
	}, [computeDirty, confirmDiscard]);

	// One registration for the form's life; everything it reads is a ref.
	const registry = useUnsavedChangesRegistry();
	useEffect(() => {
		if (!registry) return undefined;
		return registry.register({
			blocks(currentLocation, nextLocation) {
				if (!computeDirty()) return false;
				const custom = latest.current.shouldBlock;
				return (typeof custom === 'function' ? custom : leavesThePage)({ currentLocation, nextLocation });
			},
			get onBlocked() {
				return latest.current.onBlocked;
			},
			accept: allowNavigation,
		});
	}, [registry, computeDirty, allowNavigation]);

	useBeforeUnloadPrompt(isDirty, message);

	return {
		isDirty,
		resetDirty,
		allowNavigation,
		confirmDiscard,
		guardDiscard,
	};
}

function useBeforeUnloadPrompt(when, message) {
	useEffect(() => {
		if (!when) return undefined;

		const handler = (event) => {
			event.preventDefault();
			event.returnValue = message;
			return message;
		};

		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [when, message]);
}
